import { PlanEntryActiveListItem } from "../../../../common/services/plan-entry/active-list/plan-entry-active-list";
import listingFilterService from "../../../../common/utils/listing/filters/listing-filter.service";
import RouteActiveListingFilter, {
  RouteActiveListingCargoOrderHumanIdFilter,
  RouteActiveListingDriverFilter,
  RouteActiveListingPassengerFilter,
  RouteActiveListingRouteAddressFilter,
  RouteActiveListingHumanIdFilter,
  RouteActiveListingTaxiCorporationFilter,
} from "./types/route-active-listing-filter";
import RouteActiveListingFilterType from "./types/route-active-listing-filter-type";

const filterByCargoOrderHumanId = (
  listingItems: PlanEntryActiveListItem[],
  queries: RouteActiveListingCargoOrderHumanIdFilter["value"][],
): PlanEntryActiveListItem[] => {
  return listingFilterService.filterByArrayOfNumbersProperty(
    listingItems,
    "cargoOrderHumanIds",
    queries,
  );
};

const filterByDriver = (
  listingItems: PlanEntryActiveListItem[],
  queries: RouteActiveListingDriverFilter["value"][],
): PlanEntryActiveListItem[] => {
  return listingFilterService.filterByStringProperty(
    listingItems,
    "driverName",
    queries,
  );
};

const filterByPassenger = (
  listingItems: PlanEntryActiveListItem[],
  queries: RouteActiveListingPassengerFilter["value"][],
): PlanEntryActiveListItem[] => {
  return listingFilterService.filterByArrayOfStringsProperty(
    listingItems,
    "passengers",
    queries,
  );
};

const filterByRouteAddress = (
  listingItems: PlanEntryActiveListItem[],
  queries: RouteActiveListingRouteAddressFilter["value"][],
): PlanEntryActiveListItem[] => {
  let filteredListingItems: PlanEntryActiveListItem[] = [];

  queries.forEach((query) => {
    const newFilteredItems = listingItems.filter((item) =>
      item.routeAddresses
        .flat()
        .some((address) =>
          address.trim().toLowerCase().includes(query.trim().toLowerCase()),
        ),
    );

    filteredListingItems = [...filteredListingItems, ...newFilteredItems];
  });

  return filteredListingItems;
};

const filterByHumanId = (
  listingItems: PlanEntryActiveListItem[],
  queries: RouteActiveListingHumanIdFilter["value"][],
): PlanEntryActiveListItem[] => {
  return listingFilterService.filterByNumberProperty(
    listingItems,
    "humanId",
    queries,
  );
};

const filterByTaxiCorporation = (
  listingItems: PlanEntryActiveListItem[],
  queries: RouteActiveListingTaxiCorporationFilter["value"][],
): PlanEntryActiveListItem[] => {
  let filteredListingItems: PlanEntryActiveListItem[] = [];

  queries.forEach((query) => {
    const newFilteredItems = listingItems.filter((item) =>
      item.taxiCorporationName
        ?.trim()
        .toLowerCase()
        .includes(query.trim().toLowerCase()),
    );

    filteredListingItems = [...filteredListingItems, ...newFilteredItems];
  });

  return filteredListingItems;
};

const filterListingItems = (
  listingItems: PlanEntryActiveListItem[],
  filters: RouteActiveListingFilter[],
): PlanEntryActiveListItem[] => {
  if (!filters.length) {
    return [...listingItems];
  }

  let filteredListingItems: PlanEntryActiveListItem[] = [...listingItems];

  const cargoOrderHumanIdFilterValues =
    listingFilterService.getValuesOfGivenFilterType<
      RouteActiveListingFilterType,
      RouteActiveListingCargoOrderHumanIdFilter["value"]
    >(filters, RouteActiveListingFilterType.CARGO_ORDER_HUMAN_ID);

  if (cargoOrderHumanIdFilterValues.length) {
    filteredListingItems = filterByCargoOrderHumanId(
      filteredListingItems,
      cargoOrderHumanIdFilterValues,
    );
  }

  const driverFilterValues = listingFilterService.getValuesOfGivenFilterType<
    RouteActiveListingFilterType,
    RouteActiveListingDriverFilter["value"]
  >(filters, RouteActiveListingFilterType.DRIVER);

  if (driverFilterValues.length) {
    filteredListingItems = filterByDriver(
      filteredListingItems,
      driverFilterValues,
    );
  }

  const passengersFilterValues =
    listingFilterService.getValuesOfGivenFilterType<
      RouteActiveListingFilterType,
      RouteActiveListingPassengerFilter["value"]
    >(filters, RouteActiveListingFilterType.PASSENGER);

  if (passengersFilterValues.length) {
    filteredListingItems = filterByPassenger(
      filteredListingItems,
      passengersFilterValues,
    );
  }

  const routeAddressFilterValues =
    listingFilterService.getValuesOfGivenFilterType<
      RouteActiveListingFilterType,
      RouteActiveListingRouteAddressFilter["value"]
    >(filters, RouteActiveListingFilterType.ROUTE_ADDRESS);

  if (routeAddressFilterValues.length) {
    filteredListingItems = filterByRouteAddress(
      filteredListingItems,
      routeAddressFilterValues,
    );
  }

  const routeIdFilterValues = listingFilterService.getValuesOfGivenFilterType<
    RouteActiveListingFilterType,
    RouteActiveListingHumanIdFilter["value"]
  >(filters, RouteActiveListingFilterType.HUMAN_ID);

  if (routeIdFilterValues.length) {
    filteredListingItems = filterByHumanId(
      filteredListingItems,
      routeIdFilterValues,
    );
  }

  const taxiCorporationFilterValues =
    listingFilterService.getValuesOfGivenFilterType<
      RouteActiveListingFilterType,
      RouteActiveListingTaxiCorporationFilter["value"]
    >(filters, RouteActiveListingFilterType.TAXI_CORPORATION);

  if (taxiCorporationFilterValues.length) {
    filteredListingItems = filterByTaxiCorporation(
      filteredListingItems,
      taxiCorporationFilterValues,
    );
  }

  return filteredListingItems;
};

const routeActiveListingFilteringService = {
  filterListingItems,
};

export default routeActiveListingFilteringService;
